/**
 * FinancialChartWidget - Display financial data with multi-series charts
 *
 * Supports:
 * - Multiple chart types (column, line, area, spline)
 * - Dual Y-axis (left for absolute values, right for percentages)
 * - Multi-ticker comparison
 * - Interactive tooltips and legends
 */

import { useState, useRef, useMemo, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getChartTheme, CHART_COLORS, detectDarkMode, observeDarkMode, WIDGET_BACKGROUND } from './chartTheme';
import { callWidgetApi } from '../../utils/widgetApi';
import { useContainerWidth } from './hooks/useContainerWidth';

// Use shared chart colors
const SERIES_COLORS = CHART_COLORS;

// Format large numbers with Vietnamese abbreviations
function formatCompactNumber(value) {
    if (value === null || value === undefined) return '-';

    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    // Nghìn tỷ (trillion) = 1,000,000,000,000
    if (absValue >= 1_000_000_000_000) {
        return `${sign}${(absValue / 1_000_000_000_000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} nghìn tỷ`;
    }
    // Tỷ (billion) = 1,000,000,000
    if (absValue >= 1_000_000_000) {
        return `${sign}${(absValue / 1_000_000_000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} tỷ`;
    }
    // Triệu (million) = 1,000,000
    if (absValue >= 1_000_000) {
        return `${sign}${(absValue / 1_000_000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} tr`;
    }
    // Nghìn (thousand) = 1,000
    if (absValue >= 1_000) {
        return `${sign}${(absValue / 1_000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} K`;
    }

    return value.toLocaleString('vi-VN', { maximumFractionDigits: 2 });
}

// Format value based on unit type
function formatValue(value, format = 'number') {
    if (value === null || value === undefined) return '-';

    switch (format) {
        case 'billion':
            return formatCompactNumber(value);
        case 'percent':
            return `${value.toFixed(2)}%`;
        case 'ratio':
            return `${value.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}x`;
        case 'million':
            return formatCompactNumber(value);
        case 'vnd':
            return `${value.toLocaleString('vi-VN', { maximumFractionDigits: 0 })} đ`;
        default:
            return formatCompactNumber(value);
    }
}

// Detect value format from indicator name
function detectFormat(indicatorName) {
    const name = indicatorName.toLowerCase();

    // Percent (%) - ROE, ROA, margins, growth rates
    if (name.includes('%') || name.includes('tăng trưởng') || name.includes('biên') ||
        name.includes('roe') || name.includes('roa') || name.includes('tỷ lệ') ||
        name.includes('nim') || name.includes('cir') || name.includes('casa') ||
        name.includes('margin') || name.includes('yield')) {
        return 'percent';
    }

    // Ratio (lần) - P/E, P/B, EV/EBITDA, etc.
    if (name.includes('p/e') || name.includes('p/b') || name.includes('ev/') ||
        name.includes('pe ') || name.includes('pb ') || name.includes(' pe') || name.includes(' pb') ||
        name.includes('lần') || name.includes('hệ số') || name.includes('ratio')) {
        return 'ratio';
    }

    // Billion (tỷ đồng) - revenue, profit, assets
    if (name.includes('tỷ đồng') || name.includes('doanh thu') || name.includes('lợi nhuận') ||
        name.includes('tài sản') || name.includes('vốn') || name.includes('nợ') ||
        name.includes('chi phí') || name.includes('thu nhập')) {
        return 'billion';
    }

    // Million (triệu)
    if (name.includes('triệu cp') || name.includes('triệu cổ')) {
        return 'million';
    }

    // VND per share
    if (name.includes('vnđ/cp') || name.includes('vnd/cp') || name.includes('eps') ||
        name.includes('giá trị sổ sách') || name.includes('bvps') || name.includes('đồng/cp')) {
        return 'vnd';
    }

    return 'number';
}

// Build Highcharts series from config
// categories: the unified list of periods (all series must align to this)
function buildChartSeries(data, seriesConfig, ticker, categories = []) {
    const series = [];

    if (!data || !seriesConfig || seriesConfig.length === 0) {
        return series;
    }

    // Get report data (flatten all report types)
    const reportData = {};
    ['ratios', 'balance_sheet', 'income_statement', 'cash_flow'].forEach(reportType => {
        if (data[reportType]) {
            Object.assign(reportData, data[reportType]);
        }
    });

    // Build series from config
    seriesConfig.forEach((config, index) => {
        const { indicator, type = 'column', yAxis = 'left' } = config;
        const indicatorData = reportData[indicator];

        if (!indicatorData || indicatorData.length === 0) {
            console.warn(`No data found for indicator: ${indicator}`);
            return;
        }

        const periodValueMap = {};
        indicatorData.forEach(d => {
            if (d.periodDateName && d.value !== undefined && d.value !== null) {
                periodValueMap[d.periodDateName] = d.value;
            }
        });

        if (Object.keys(periodValueMap).length === 0) {
            return;
        }

        const alignedData = categories.map(period => {
            const value = periodValueMap[period];
            return value !== undefined ? value : null;
        });

        const yAxisIndex = yAxis === 'right' ? 1 : 0;

        series.push({
            name: ticker ? `${indicator} (${ticker})` : indicator,
            type: type === 'column' ? 'column' : type,
            yAxis: yAxisIndex,
            data: alignedData,
            color: SERIES_COLORS[index % SERIES_COLORS.length],
            marker: { enabled: type === 'line' || type === 'spline', radius: 4 },
        });
    });

    return series;
}

// Helper: parse period string to sortable number
function parsePeriodToNumber(period) {
    if (!period) return 0;
    const parts = period.split('/');
    if (parts.length === 2) {
        const q = parseInt(parts[0].replace('Q', '')) || 1;
        const y = parseInt(parts[1]) || 2000;
        return y * 4 + q;
    }
    return parseInt(period) || 0;
}

// Build categories (periods) from data - collect ALL unique periods from ALL indicators
function buildCategories(data, seriesConfig) {
    if (!data || !seriesConfig || seriesConfig.length === 0) {
        return [];
    }

    // Get report data
    const reportData = {};
    ['ratios', 'balance_sheet', 'income_statement', 'cash_flow'].forEach(reportType => {
        if (data[reportType]) {
            Object.assign(reportData, data[reportType]);
        }
    });

    // Collect ALL unique periods from ALL selected indicators
    const periodSet = new Set();
    seriesConfig.forEach(config => {
        const indicatorData = reportData[config.indicator];
        if (indicatorData && Array.isArray(indicatorData)) {
            indicatorData.forEach(d => {
                if (d.periodDateName) {
                    periodSet.add(d.periodDateName);
                }
            });
        }
    });

    // Convert to array and sort
    const periods = Array.from(periodSet);
    periods.sort((a, b) => parsePeriodToNumber(a) - parsePeriodToNumber(b));

    return periods;
}

// Chart type options
const CHART_TYPES = [
    { value: 'column', label: 'Cột' },
    { value: 'line', label: 'Đường' },
    { value: 'area', label: 'Vùng' },
    { value: 'spline', label: 'Đường cong' },
];

// Y-Axis options
const YAXIS_OPTIONS = [
    { value: 'left', label: 'Trục trái' },
    { value: 'right', label: 'Trục phải' },
];

// Format/Unit options - matching MCP VALUE_FORMAT enum
const FORMAT_OPTIONS = [
    { value: 'auto', label: 'Tự động' },
    { value: 'percent', label: '%' },
    { value: 'ratio', label: 'Lần (x)' },
    { value: 'billion', label: 'Tỷ đồng' },
    { value: 'million', label: 'Triệu' },
    { value: 'vnd', label: 'VNĐ/CP' },
    { value: 'number', label: 'Số' },
];

// Period options
const PERIOD_OPTIONS = [
    { value: 'Q', label: 'Quý' },
    { value: 'Y', label: 'Năm' },
    // TODO: Add LTM support when MCP tool supports it
    // { value: 'LTM', label: 'Lũy kế 12 tháng (LTM)' },
];

// Size options (number of periods)
const SIZE_OPTIONS = [4, 8, 12, 16, 20];

// Get all available indicators from data
function getAvailableIndicators(data) {
    const indicators = [];
    if (!data) return indicators;

    // For single ticker: data = { ticker, ratios, balance_sheet, ... }
    // For multi-ticker: data = { HPG: {...}, VNM: {...} }
    const isSingleTicker = data.ticker !== undefined;
    const tickerData = isSingleTicker ? data : Object.values(data)[0];

    if (!tickerData) return indicators;

    ['ratios', 'balance_sheet', 'income_statement', 'cash_flow'].forEach(reportType => {
        const reportData = tickerData[reportType];
        if (reportData) {
            Object.keys(reportData).forEach(key => {
                const entries = reportData[key];
                if (!key.startsWith('_') && entries?.length > 0) {
                    const hasAnyValue = entries.some(d => d.value !== undefined && d.value !== null);
                    if (hasAnyValue) {
                        indicators.push({
                            name: key,
                            reportType,
                            format: detectFormat(key),
                        });
                    }
                }
            });
        }
    });

    return indicators;
}

/**
 * Main Financial Chart Widget
 */
export default function FinancialChartWidget({ data: initialData, config }) {
    const chartRef = useRef(null);
    const [showSettings, setShowSettings] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedData, setFetchedData] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(detectDarkMode);
    const [tickerLogos, setTickerLogos] = useState({}); // { ticker: logoUrl }

    // Use reusable hook for responsive container width detection
    const { containerRef, isMobile } = useContainerWidth(500);

    // Responsive chart height: smaller on mobile
    const chartHeight = isMobile ? 220 : 300;

    // Observe dark mode changes
    useEffect(() => {
        return observeDarkMode(setIsDarkMode);
    }, []);

    // Extract config - series can be at root level or inside params
    const { params = {}, series: rootSeries } = config || {};
    const { tickers: rawTickers = [], series: paramsSeries = [], period: initialPeriod, size: initialSize, report_types } = params;
    const tickers = Array.isArray(rawTickers) ? rawTickers : (rawTickers ? [rawTickers] : []);

    // User-adjustable period and size
    const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod || 'Q');
    // Ensure initialSize is a valid option, default to 8 if not
    const [selectedSize, setSelectedSize] = useState(() => {
        const validSizes = [4, 8, 12, 16, 20];
        return validSizes.includes(initialSize) ? initialSize : 8;
    });

    // Selected tickers for comparison (default: first ticker only)
    const [selectedTickers, setSelectedTickers] = useState(() => {
        return tickers.length > 0 ? [tickers[0]] : [];
    });

    // Ticker popover state
    const [showTickerPopover, setShowTickerPopover] = useState(false);
    const tickerPopoverRef = useRef(null);

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tickerPopoverRef.current && !tickerPopoverRef.current.contains(event.target)) {
                setShowTickerPopover(false);
            }
        };
        if (showTickerPopover) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTickerPopover]);

    // Fetch ticker logos
    useEffect(() => {
        if (tickers.length === 0) return;

        const fetchLogos = async () => {
            const apiBaseUrl = import.meta.env.VITE_AUTH_BASE_URL || 'https://api.simplize.dev';
            const logos = {};

            await Promise.all(tickers.map(async (ticker) => {
                try {
                    const res = await fetch(`${apiBaseUrl}/api/company/summary/${ticker}`);
                    if (res.ok) {
                        const response = await res.json();
                        const data = response.data || response;
                        if (data.imageUrl) {
                            logos[ticker] = data.imageUrl;
                        }
                    }
                } catch (err) {
                    // Ignore errors, will use fallback
                }
            }));

            setTickerLogos(logos);
        };

        fetchLogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickers.join(',')]);

    // Use fetched data if available, otherwise use initial data
    const data = fetchedData || initialData;

    // Request counter to handle race conditions (only accept latest request)
    const requestIdRef = useRef(0);
    const isLoadingRef = useRef(false);

    // Fetch new data when period or size changes
    const fetchData = async (newPeriod, newSize) => {
        if (!tickers.length) return;

        // Skip if already loading (prevent duplicate calls)
        if (isLoadingRef.current) {
            console.log('Skip fetch - already loading');
            return;
        }

        const currentRequestId = ++requestIdRef.current;
        isLoadingRef.current = true;
        setIsLoading(true);

        try {
            const result = await callWidgetApi('get_financial_reports', {
                tickers,
                report_types: report_types || ['ratios', 'income_statement', 'balance_sheet', 'cash_flow'],
                period: newPeriod,
                size: newSize,
            });

            // Only update if this is still the latest request
            if (currentRequestId === requestIdRef.current && result.data) {
                setFetchedData(result.data);
            }
        } catch (error) {
            console.error('Failed to fetch financial data:', error);
        } finally {
            // Only clear loading if this is still the latest request
            if (currentRequestId === requestIdRef.current) {
                isLoadingRef.current = false;
                setIsLoading(false);
            }
        }
    };

    // Handle period change
    const handlePeriodChange = (newPeriod) => {
        setSelectedPeriod(newPeriod);
        // Reset loading ref to allow new fetch
        isLoadingRef.current = false;
        fetchData(newPeriod, selectedSize);
    };

    // Handle size change
    const handleSizeChange = (newSize) => {
        setSelectedSize(newSize);
        // Reset loading ref to allow new fetch
        isLoadingRef.current = false;
        fetchData(selectedPeriod, newSize);
    };

    // Fetch data on mount to ensure correct size
    // isLoadingRef prevents duplicate calls, requestIdRef handles race conditions
    useEffect(() => {
        if (tickers.length === 0) return;
        fetchData(selectedPeriod, selectedSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial series from LLM config
    const initialSeriesConfig = rootSeries || paramsSeries || [];

    // User-editable series config (initialized from LLM config)
    // format: 'auto' = use auto-detection, or user can override with specific format
    const [selectedSeries, setSelectedSeries] = useState(() => {
        return initialSeriesConfig.map((s, i) => ({
            indicator: s.indicator,
            type: s.type || 'column',
            yAxis: s.yAxis || 'left',
            format: s.format || 'auto', // 'auto' means use detectFormat()
            id: `series-${i}`,
        }));
    });

    // Get all available indicators
    const availableIndicators = useMemo(() => getAvailableIndicators(data), [data]);

    // Auto-select default indicators from priority list
    const selectDefaultIndicators = (indicators) => {
        const priorityIndicators = [
            'Doanh thu thuần',
            'Doanh thu',
            'Lợi nhuận sau thuế',
            'Lợi nhuận ròng',
            'P/E',
            'ROE LTM',
            'ROE',
        ];

        const defaultIndicators = [];
        for (const name of priorityIndicators) {
            const found = indicators.find(ind =>
                ind.name.toLowerCase().includes(name.toLowerCase())
            );
            if (found && !defaultIndicators.some(d => d.name === found.name)) {
                defaultIndicators.push(found);
                if (defaultIndicators.length >= 2) break;
            }
        }

        if (defaultIndicators.length < 2) {
            for (const ind of indicators) {
                if (!defaultIndicators.some(d => d.name === ind.name)) {
                    defaultIndicators.push(ind);
                    if (defaultIndicators.length >= 2) break;
                }
            }
        }

        return defaultIndicators.map((ind, i) => {
            const isRatioOrPercent = ind.format === 'percent' || ind.format === 'ratio';
            return {
                indicator: ind.name,
                type: isRatioOrPercent ? 'line' : 'column',
                yAxis: isRatioOrPercent ? 'right' : 'left',
                format: 'auto',
                id: `series-default-${i}`,
            };
        });
    };

    // Auto-select or validate LLM series against available data
    const hasInitializedDefaults = useRef(false);
    useEffect(() => {
        if (availableIndicators.length === 0 || hasInitializedDefaults.current) return;

        const availableNames = new Set(availableIndicators.map(ind => ind.name));

        if (initialSeriesConfig.length === 0 && selectedSeries.length === 0) {
            hasInitializedDefaults.current = true;
            const defaults = selectDefaultIndicators(availableIndicators);
            if (defaults.length > 0) setSelectedSeries(defaults);
            return;
        }

        if (initialSeriesConfig.length > 0) {
            const validSeries = selectedSeries.filter(s => availableNames.has(s.indicator));
            if (validSeries.length === 0) {
                hasInitializedDefaults.current = true;
                const defaults = selectDefaultIndicators(availableIndicators);
                if (defaults.length > 0) setSelectedSeries(defaults);
            } else if (validSeries.length < selectedSeries.length) {
                hasInitializedDefaults.current = true;
                setSelectedSeries(validSeries);
            }
        }
    }, [availableIndicators, initialSeriesConfig.length, selectedSeries.length]);

    // Remove Vietnamese diacritics for search
    const removeVietnameseDiacritics = (str) => {
        return str
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/đ/g, 'd')
            .replace(/Đ/g, 'D');
    };

    // Normalize string for flexible search (remove special chars, diacritics, lowercase)
    const normalizeForSearch = (str) => {
        return removeVietnameseDiacritics(str).toLowerCase().replace(/[\/\-_\s]/g, '');
    };

    // Filtered indicators based on search (supports flexible matching: "pb" → "p/b")
    const filteredIndicators = useMemo(() => {
        if (!searchTerm) return availableIndicators;
        const term = searchTerm.toLowerCase();
        const normalizedTerm = normalizeForSearch(searchTerm);
        return availableIndicators.filter(ind => {
            const name = ind.name.toLowerCase();
            const normalizedName = normalizeForSearch(ind.name);
            // Match either exact or normalized (pb → p/b, pe → p/e)
            return name.includes(term) || normalizedName.includes(normalizedTerm);
        });
    }, [availableIndicators, searchTerm]);

    // Check if an indicator is selected
    const isIndicatorSelected = (indicatorName) => {
        return selectedSeries.some(s => s.indicator === indicatorName);
    };

    // Toggle indicator selection
    const toggleIndicator = (indicator) => {
        if (isIndicatorSelected(indicator.name)) {
            setSelectedSeries(prev => prev.filter(s => s.indicator !== indicator.name));
        } else {
            // Ratio (P/E, P/B) and percent (ROA, ROE) indicators default to line chart
            const isRatioOrPercent = indicator.format === 'percent' || indicator.format === 'ratio';
            const defaultType = isRatioOrPercent ? 'line' : 'column';
            const defaultYAxis = isRatioOrPercent ? 'right' : 'left';
            setSelectedSeries(prev => [...prev, {
                indicator: indicator.name,
                type: defaultType,
                yAxis: defaultYAxis,
                format: 'auto', // Use auto-detection by default, user can override
                id: `series-${Date.now()}`,
            }]);
        }
    };

    // Update series chart type
    const updateSeriesType = (indicatorName, newType) => {
        setSelectedSeries(prev => prev.map(s =>
            s.indicator === indicatorName ? { ...s, type: newType } : s
        ));
    };

    // Update series Y-axis
    const updateSeriesYAxis = (indicatorName, newYAxis) => {
        setSelectedSeries(prev => prev.map(s =>
            s.indicator === indicatorName ? { ...s, yAxis: newYAxis } : s
        ));
    };

    // Update series format (unit type)
    const updateSeriesFormat = (indicatorName, newFormat) => {
        setSelectedSeries(prev => prev.map(s =>
            s.indicator === indicatorName ? { ...s, format: newFormat } : s
        ));
    };

    // Get effective format (use user override if not 'auto', otherwise auto-detect)
    const getEffectiveFormat = (series) => {
        if (series.format && series.format !== 'auto') {
            return series.format;
        }
        return detectFormat(series.indicator);
    };

    // Use selectedSeries for chart (editable by user)
    const seriesConfig = selectedSeries;

    // Determine if we have series config
    const hasSeriesConfig = seriesConfig && seriesConfig.length > 0;

    // Build chart options
    const options = useMemo(() => {
        if (!data) return null;

        // For multi-ticker, data is { ticker: {...}, ticker2: {...} }
        // For single ticker, data is { ticker, ratios, balance_sheet, ... }
        const isSingleTicker = data.ticker !== undefined;
        const allTickerData = isSingleTicker ? { [data.ticker]: data } : data;

        // Filter to only selected tickers
        const tickerData = {};
        selectedTickers.forEach(ticker => {
            if (allTickerData[ticker]) {
                tickerData[ticker] = allTickerData[ticker];
            }
        });

        // If no tickers selected, show nothing
        if (Object.keys(tickerData).length === 0) {
            return null;
        }

        // If no series config, show a message
        if (!hasSeriesConfig) {
            return null;
        }

        // Build format map for tooltip (use effective format with user overrides)
        const indicatorFormats = {};
        seriesConfig.forEach(s => {
            indicatorFormats[s.indicator] = getEffectiveFormat(s);
        });

        // Get categories from first selected ticker's data
        const firstTicker = Object.keys(tickerData)[0];
        const categories = buildCategories(tickerData[firstTicker], seriesConfig);

        // Build all series (temporarily with yAxis as 0/1 for left/right)
        let allSeries = [];
        Object.entries(tickerData).forEach(([ticker, tickerDataItem]) => {
            const tickerSeries = buildChartSeries(
                tickerDataItem,
                seriesConfig,
                Object.keys(tickerData).length > 1 ? ticker : null,
                categories
            );
            allSeries = allSeries.concat(tickerSeries);
        });

        // Group series by (side, format) to create separate Y-axes for each unique format
        // This ensures different units (%, x, tỷ) get their own Y-axis
        const axisGroups = new Map(); // key: "side-format" -> { side, format, indicators: Set }
        seriesConfig.forEach(s => {
            const side = s.yAxis === 'right' ? 'right' : 'left';
            const format = getEffectiveFormat(s);
            const key = `${side}-${format}`;
            if (!axisGroups.has(key)) {
                axisGroups.set(key, { side, format, indicators: new Set() });
            }
            axisGroups.get(key).indicators.add(s.indicator);
        });

        // Create Y-axes: first all left axes, then all right axes
        const yAxisConfigs = [];
        const axisKeyToIndex = new Map();

        // Label color based on dark mode
        const labelColor = isDarkMode ? '#e5e5e5' : '#333333';

        ['left', 'right'].forEach(side => {
            let sideAxisCount = 0;
            axisGroups.forEach((group, key) => {
                if (group.side === side) {
                    const axisIndex = yAxisConfigs.length;
                    axisKeyToIndex.set(key, axisIndex);

                    yAxisConfigs.push({
                        title: { text: null },
                        opposite: side === 'right',
                        labels: {
                            style: { color: labelColor, fontSize: '11px' },
                            formatter: function() {
                                return formatValue(this.value, group.format);
                            },
                        },
                        // No grid lines - clean look like StockInfoCard
                        gridLineWidth: 0,
                    });
                    sideAxisCount++;
                }
            });
        });

        // Ensure at least 2 axes exist (left and right) for proper layout
        if (yAxisConfigs.length === 0) {
            yAxisConfigs.push(
                { title: { text: null }, opposite: false, gridLineWidth: 0, labels: { style: { color: labelColor } } },
                { title: { text: null }, opposite: true, gridLineWidth: 0, labels: { style: { color: labelColor } } }
            );
        } else if (yAxisConfigs.length === 1) {
            // Add a placeholder axis on the opposite side
            const existingSide = yAxisConfigs[0].opposite ? 'right' : 'left';
            if (existingSide === 'left') {
                yAxisConfigs.push({ title: { text: null }, opposite: true, gridLineWidth: 0, labels: { style: { color: labelColor } } });
            } else {
                yAxisConfigs.unshift({ title: { text: null }, opposite: false, gridLineWidth: 0, labels: { style: { color: labelColor } } });
                // Shift all indices by 1
                axisKeyToIndex.forEach((idx, key) => axisKeyToIndex.set(key, idx + 1));
            }
        }

        // Update series yAxis indices based on their indicator's (side, format)
        const finalSeries = allSeries.map(series => {
            const seriesName = series.name;
            const indicatorName = seriesName.includes('(') ? seriesName.split(' (')[0] : seriesName;
            const config = seriesConfig.find(s => s.indicator === indicatorName);
            if (config) {
                const side = config.yAxis === 'right' ? 'right' : 'left';
                const format = getEffectiveFormat(config);
                const key = `${side}-${format}`;
                const axisIdx = axisKeyToIndex.get(key);
                if (axisIdx !== undefined) {
                    return { ...series, yAxis: axisIdx };
                }
            }
            return series;
        });

        // Get shared theme config
        const theme = getChartTheme(isDarkMode);

        return {
            chart: {
                ...theme.chart,
                height: chartHeight,
            },
            title: { text: null },
            credits: theme.credits,
            legend: theme.legend,
            xAxis: {
                ...theme.xAxis,
                categories,
            },
            yAxis: yAxisConfigs.map((axisConfig) => ({
                ...theme.yAxis,
                ...axisConfig,
                labels: {
                    ...theme.yAxis.labels,
                    ...axisConfig.labels,
                    style: {
                        ...theme.yAxis.labels.style,
                        ...(axisConfig.labels?.style || {}),
                    },
                },
            })),
            tooltip: {
                ...theme.tooltip,
                crosshairs: true,
                formatter: function() {
                    const textColor = isDarkMode ? 'rgba(229, 229, 229, 0.9)' : 'rgba(51, 51, 51, 0.9)';
                    const mutedColor = isDarkMode ? 'rgba(229, 229, 229, 0.6)' : 'rgba(51, 51, 51, 0.6)';
                    // Get the category name (period) from the first point
                    const categoryName = this.points[0]?.key || categories[this.points[0]?.point?.index] || this.x;
                    let html = `<div style="padding: 2px 0"><div style="font-weight: 500; margin-bottom: 8px; color: ${textColor}">${categoryName}</div>`;
                    this.points.forEach(point => {
                        const seriesName = point.series.name;
                        const indicatorName = seriesName.includes('(') ? seriesName.split(' (')[0] : seriesName;
                        const format = indicatorFormats[indicatorName] || 'number';
                        html += `
                            <div style="display: flex; align-items: center; gap: 8px; font-size: 12px; margin: 4px 0">
                                <span style="color:${point.color}">●</span>
                                <span style="color: ${mutedColor}">${point.series.name}:</span>
                                <span style="font-weight: 500; color: ${textColor}">${formatValue(point.y, format)}</span>
                            </div>
                        `;
                    });
                    html += '</div>';
                    return html;
                },
            },
            plotOptions: theme.plotOptions,
            series: finalSeries,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, selectedSeries, selectedTickers, hasSeriesConfig, isDarkMode, chartHeight]);

    // No series config - show indicator selector
    if (!hasSeriesConfig && data) {
        return (
            <div ref={containerRef} className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {/* Selected tickers as chips with logo */}
                            {selectedTickers.map((ticker, idx) => {
                                const logoUrl = tickerLogos[ticker];
                                return (
                                    <button
                                        key={ticker}
                                        onClick={() => {
                                            if (selectedTickers.length > 1) {
                                                setSelectedTickers(prev => prev.filter(t => t !== ticker));
                                            }
                                        }}
                                        className="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-full border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 transition-all whitespace-nowrap"
                                        title={selectedTickers.length > 1 ? `Bỏ chọn ${ticker}` : ticker}
                                    >
                                        {logoUrl ? (
                                            <img
                                                src={logoUrl}
                                                alt={ticker}
                                                className="w-5 h-5 rounded-full object-contain flex-shrink-0"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div
                                            className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${logoUrl ? 'hidden' : ''}`}
                                            style={{ backgroundColor: SERIES_COLORS[idx % SERIES_COLORS.length] }}
                                        >
                                            {ticker?.charAt(0)}
                                        </div>
                                        <span className="font-medium text-xs text-gray-900 dark:text-white">
                                            {ticker}
                                        </span>
                                    </button>
                                );
                            })}
                            {/* Add ticker button with popover */}
                            {tickers.length > 1 && (
                                <div className="relative" ref={tickerPopoverRef}>
                                    <button
                                        onClick={() => setShowTickerPopover(!showTickerPopover)}
                                        className="w-7 h-7 flex items-center justify-center text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                        title="Chọn mã để so sánh"
                                    >
                                        +
                                    </button>
                                    {/* Popover */}
                                    {showTickerPopover && (
                                        <div className="absolute top-full left-0 mt-1 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[140px]">
                                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                                Chọn mã so sánh
                                            </div>
                                            <div className="space-y-1">
                                                {tickers.map((ticker, idx) => {
                                                    const isSelected = selectedTickers.includes(ticker);
                                                    const canDeselect = selectedTickers.length > 1;
                                                    const logoUrl = tickerLogos[ticker];
                                                    return (
                                                        <label
                                                            key={ticker}
                                                            className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                                                isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                                                            }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => {
                                                                    if (isSelected && canDeselect) {
                                                                        setSelectedTickers(prev => prev.filter(t => t !== ticker));
                                                                    } else if (!isSelected && selectedTickers.length < 5) {
                                                                        setSelectedTickers(prev => [...prev, ticker]);
                                                                    }
                                                                }}
                                                                disabled={(isSelected && !canDeselect) || (!isSelected && selectedTickers.length >= 5)}
                                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                            />
                                                            {logoUrl ? (
                                                                <img src={logoUrl} alt={ticker} className="w-5 h-5 rounded-full object-contain" />
                                                            ) : (
                                                                <div
                                                                    className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                                                    style={{ backgroundColor: SERIES_COLORS[idx % SERIES_COLORS.length] }}
                                                                >
                                                                    {ticker?.charAt(0)}
                                                                </div>
                                                            )}
                                                            <span className={isSelected ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'}>
                                                                {ticker}
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Period Dropdown */}
                            <select
                                value={selectedPeriod}
                                onChange={(e) => handlePeriodChange(e.target.value)}
                                className="px-2 py-1 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border-none rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {PERIOD_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            {/* Size Dropdown */}
                            <select
                                value={selectedSize}
                                onChange={(e) => handleSizeChange(Number(e.target.value))}
                                className="px-2 py-1 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border-none rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {SIZE_OPTIONS.map(s => (
                                    <option key={s} value={s}>{s} kỳ</option>
                                ))}
                            </select>
                            {isLoading && (
                                <svg className="w-4 h-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Chọn chỉ số để hiển thị biểu đồ ({availableIndicators.length} khả dụng)
                    </div>

                    {/* Search */}
                    <div className="mb-3">
                        <input
                            type="text"
                            placeholder="Tìm chỉ số..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Available Indicators */}
                    <div className="max-h-60 overflow-y-auto">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                            {filteredIndicators.slice(0, 50).map((ind) => (
                                <label
                                    key={ind.name}
                                    className={`flex items-center gap-2 px-2 py-1.5 text-xs rounded cursor-pointer transition-colors ${
                                        isIndicatorSelected(ind.name)
                                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isIndicatorSelected(ind.name)}
                                        onChange={() => toggleIndicator(ind)}
                                        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="truncate" title={ind.name}>{ind.name}</span>
                                </label>
                            ))}
                        </div>
                        {filteredIndicators.length > 50 && (
                            <div className="text-xs text-gray-500 mt-2 text-center">
                                Hiển thị 50/{filteredIndicators.length} chỉ số. Dùng tìm kiếm để lọc thêm.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (!options) {
        return (
            <div className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 text-center text-gray-500" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                Không có dữ liệu để hiển thị
            </div>
        );
    }

    return (
        <div ref={containerRef} className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden" style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {/* Selected tickers as chips with logo */}
                        {selectedTickers.map((ticker, idx) => {
                            const logoUrl = tickerLogos[ticker];
                            return (
                                <button
                                    key={ticker}
                                    onClick={() => {
                                        if (selectedTickers.length > 1) {
                                            setSelectedTickers(prev => prev.filter(t => t !== ticker));
                                        }
                                    }}
                                    className="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-full border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 transition-all whitespace-nowrap"
                                    title={selectedTickers.length > 1 ? `Bỏ chọn ${ticker}` : ticker}
                                >
                                    {logoUrl ? (
                                        <img
                                            src={logoUrl}
                                            alt={ticker}
                                            className="w-5 h-5 rounded-full object-contain flex-shrink-0"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${logoUrl ? 'hidden' : ''}`}
                                        style={{ backgroundColor: SERIES_COLORS[idx % SERIES_COLORS.length] }}
                                    >
                                        {ticker?.charAt(0)}
                                    </div>
                                    <span className="font-medium text-xs text-gray-900 dark:text-white">
                                        {ticker}
                                    </span>
                                </button>
                            );
                        })}
                        {/* Add ticker button with popover */}
                        {tickers.length > 1 && (
                            <div className="relative" ref={tickerPopoverRef}>
                                <button
                                    onClick={() => setShowTickerPopover(!showTickerPopover)}
                                    className="w-7 h-7 flex items-center justify-center text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    title="Chọn mã để so sánh"
                                >
                                    +
                                </button>
                                {/* Popover */}
                                {showTickerPopover && (
                                    <div className="absolute top-full left-0 mt-1 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[140px]">
                                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                            Chọn mã so sánh
                                        </div>
                                        <div className="space-y-1">
                                            {tickers.map((ticker, idx) => {
                                                const isSelected = selectedTickers.includes(ticker);
                                                const canDeselect = selectedTickers.length > 1;
                                                const logoUrl = tickerLogos[ticker];
                                                return (
                                                    <label
                                                        key={ticker}
                                                        className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                                            isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => {
                                                                if (isSelected && canDeselect) {
                                                                    setSelectedTickers(prev => prev.filter(t => t !== ticker));
                                                                } else if (!isSelected && selectedTickers.length < 5) {
                                                                    setSelectedTickers(prev => [...prev, ticker]);
                                                                }
                                                            }}
                                                            disabled={(isSelected && !canDeselect) || (!isSelected && selectedTickers.length >= 5)}
                                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        />
                                                        {logoUrl ? (
                                                            <img src={logoUrl} alt={ticker} className="w-5 h-5 rounded-full object-contain" />
                                                        ) : (
                                                            <div
                                                                className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                                                style={{ backgroundColor: SERIES_COLORS[idx % SERIES_COLORS.length] }}
                                                            >
                                                                {ticker?.charAt(0)}
                                                            </div>
                                                        )}
                                                        <span className={isSelected ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'}>
                                                            {ticker}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Period Dropdown */}
                        <select
                            value={selectedPeriod}
                            onChange={(e) => handlePeriodChange(e.target.value)}
                            className="px-2 py-1 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border-none rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {PERIOD_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        {/* Size Dropdown */}
                        <select
                            value={selectedSize}
                            onChange={(e) => handleSizeChange(Number(e.target.value))}
                            className="px-2 py-1 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border-none rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {SIZE_OPTIONS.map(s => (
                                <option key={s} value={s}>{s} kỳ</option>
                            ))}
                        </select>
                        {/* Loading indicator */}
                        {isLoading && (
                            <svg className="w-4 h-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        )}
                    </div>
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Chọn chỉ số"
                    >
                        {/* Bar chart with plus icon - for selecting data series */}
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    {/* Search */}
                    <div className="mb-3">
                        <input
                            type="text"
                            placeholder="Tìm chỉ số..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Selected Series - Grid layout for aligned columns */}
                    {selectedSeries.length > 0 && (
                        <div className="mb-3">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                Đang hiển thị ({selectedSeries.length})
                            </div>
                            {/* Header row */}
                            <div className="grid grid-cols-[1fr_70px_120px_80px_24px] gap-1 text-[10px] text-gray-400 dark:text-gray-500 mb-1 px-2">
                                <span>Chỉ số</span>
                                <span>Biểu đồ</span>
                                <span>Đơn vị</span>
                                <span>Trục Y</span>
                                <span></span>
                            </div>
                            {/* Data rows */}
                            <div className="space-y-1">
                                {selectedSeries.map((s) => (
                                    <div
                                        key={s.id}
                                        className="grid grid-cols-[1fr_70px_120px_80px_24px] gap-1 items-center px-2 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg"
                                    >
                                        <span className="truncate" title={s.indicator}>{s.indicator}</span>
                                        <select
                                            value={s.type}
                                            onChange={(e) => updateSeriesType(s.indicator, e.target.value)}
                                            className="w-full px-1 py-0.5 text-xs bg-transparent border-none focus:outline-none cursor-pointer"
                                            title="Loại biểu đồ"
                                        >
                                            {CHART_TYPES.map(ct => (
                                                <option key={ct.value} value={ct.value}>{ct.label}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={s.format || 'auto'}
                                            onChange={(e) => updateSeriesFormat(s.indicator, e.target.value)}
                                            className="w-full px-1 py-0.5 text-xs bg-transparent border-none focus:outline-none cursor-pointer"
                                            title="Đơn vị (tự động hoặc ghi đè)"
                                        >
                                            {FORMAT_OPTIONS.map(fo => (
                                                <option key={fo.value} value={fo.value}>
                                                    {fo.value === 'auto' ? `${fo.label} (${detectFormat(s.indicator)})` : fo.label}
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            value={s.yAxis}
                                            onChange={(e) => updateSeriesYAxis(s.indicator, e.target.value)}
                                            className="w-full px-1 py-0.5 text-xs bg-transparent border-none focus:outline-none cursor-pointer"
                                            title="Trục Y"
                                        >
                                            {YAXIS_OPTIONS.map(ya => (
                                                <option key={ya.value} value={ya.value}>{ya.label}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => setSelectedSeries(prev => prev.filter(x => x.id !== s.id))}
                                            className="text-blue-500 hover:text-red-500 text-center"
                                            title="Xóa"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Available Indicators */}
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                        Chỉ số khả dụng ({filteredIndicators.length})
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                            {filteredIndicators.slice(0, 30).map((ind) => (
                                <label
                                    key={ind.name}
                                    className={`flex items-center gap-2 px-2 py-1.5 text-xs rounded cursor-pointer transition-colors ${
                                        isIndicatorSelected(ind.name)
                                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isIndicatorSelected(ind.name)}
                                        onChange={() => toggleIndicator(ind)}
                                        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="truncate" title={ind.name}>{ind.name}</span>
                                </label>
                            ))}
                        </div>
                        {filteredIndicators.length > 30 && (
                            <div className="text-xs text-gray-500 mt-2 text-center">
                                Hiển thị 30/{filteredIndicators.length} chỉ số. Dùng tìm kiếm để lọc thêm.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Chart */}
            <div className="py-2 px-3">
                <HighchartsReact
                    key={`${selectedTickers.join('-')}|${selectedSeries.map(s => `${s.indicator}-${s.type}-${s.yAxis}-${s.format}`).join('|')}`}
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={options}
                />
            </div>
        </div>
    );
}
